import React, { useId } from 'react';
import ReactMarkdown from 'react-markdown';
import Link from '@amzn/awsui-components-core/link';
import { ArrowLink } from '../ArrowLink/ArrowLink';
import { Image } from '../Image/Image';
import { SectionHeader } from '../SectionHeader/SectionHeader';
import type { CustomIconName } from '../CustomIcon/CustomIcon';
import type { CtaBehavior } from '../../utils/ctaBehavior';
import { hasCtaDestination } from '../../utils/ctaAction';
import type { CtaAction } from '../../utils/ctaAction';
import { blockAnchorAttributes } from '../../utils/blockAnchor';
import './ContentGrid.scss';

/**
 * ContentGrid - Content block for Poly CMS.
 *
 * Section header above a responsive 1/2/3/4-column grid. Each item is either
 * icon mode (iconUrl) or image mode (imageSrc). The image hero supports three
 * aspect ratios via the component-level `imageAspect` prop:
 * - `'landscape'` (default): 2:1 banner, no overlay.
 * - `'portrait'`: 4:5 portrait, supports an optional per-item overlay pill
 *   (`overlayText` + `overlayPosition`).
 * - `'square'`: 1:1, for 4-across photo grids; no overlay.
 *
 * Items are flat, indexed props (item1Heading, item1IconUrl, …) reconstructed
 * into an array internally. Supports up to 6 items; a slot renders when any of
 * its heading, text, or image is set, so an item can carry body copy without a
 * heading of its own.
 *
 * The `layout` prop chooses between the responsive N-column grid and a
 * two-column sticky left rail holding the section header and an optional
 * portrait visual beside a 1-up stack of items.
 */

/** Position of the optional 4:5 portrait-image overlay pill. */
export type ContentGridOverlayPosition =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right';

/** A single item-level CTA (text + destination). */
export interface ContentGridItemCta {
  text: string;
  href?: string;
  behavior?: CtaBehavior;
  action?: CtaAction;
  popupTargetId?: string;
}

/** Placement of the section header relative to the grid items. */
export type ContentGridLayout = 'grid' | 'sticky-rail';

/** Reconstructed grid item, assembled from the flat props. */
export interface ContentGridItem {
  heading?: string;
  text: string;
  iconUrl?: string;
  imageSrc?: string;
  imageAlt?: string;
  badge?: string;
  ctas: ContentGridItemCta[];
  overlayText?: string;
  overlayPosition?: ContentGridOverlayPosition;
}

/** Flat, per-item props for slots 1-6. */
export interface ContentGridSlotProps {
  /** Item 1 */
  item1Heading?: string;
  /** @polyType RichText */
  item1Text?: string;
  /** @polyType Url */
  item1IconUrl?: string;
  /** @polyType Url */
  item1ImageSrc?: string;
  item1ImageAlt?: string;
  item1Badge?: string;
  item1CtaText?: string;
  /** @polyType Url */
  item1CtaHref?: string;
  /**
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyTranslatable false
   */
  item1CtaBehavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Link by default. Watch video plays the URL in a player on this page. Download downloads the file when the browser permits it. Popup opens the Popup block matching the Target Popup ID.
   * @polyDefault link
   * @polyTranslatable false
   */
  item1CtaAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  item1CtaTargetPopupId?: string;
  item1Cta2Text?: string;
  /** @polyType Url */
  item1Cta2Href?: string;
  /**
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyTranslatable false
   */
  item1Cta2Behavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Link by default. Watch video plays the URL in a player on this page. Download downloads the file when the browser permits it. Popup opens the Popup block matching the Target Popup ID.
   * @polyDefault link
   * @polyTranslatable false
   */
  item1Cta2Action?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  item1Cta2TargetPopupId?: string;
  item1Cta3Text?: string;
  /** @polyType Url */
  item1Cta3Href?: string;
  /**
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyTranslatable false
   */
  item1Cta3Behavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Link by default. Watch video plays the URL in a player on this page. Download downloads the file when the browser permits it. Popup opens the Popup block matching the Target Popup ID.
   * @polyDefault link
   * @polyTranslatable false
   */
  item1Cta3Action?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  item1Cta3TargetPopupId?: string;
  item1OverlayText?: string;
  /**
   * @polyType SelectList
   * @polyOptions Top Left=top-left,Top Right=top-right,Bottom Left=bottom-left,Bottom Right=bottom-right
   */
  item1OverlayPosition?: ContentGridOverlayPosition;

  /** Item 2 */
  item2Heading?: string;
  /** @polyType RichText */
  item2Text?: string;
  /** @polyType Url */
  item2IconUrl?: string;
  /** @polyType Url */
  item2ImageSrc?: string;
  item2ImageAlt?: string;
  item2Badge?: string;
  item2CtaText?: string;
  /** @polyType Url */
  item2CtaHref?: string;
  /**
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyTranslatable false
   */
  item2CtaBehavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Link by default. Watch video plays the URL in a player on this page. Download downloads the file when the browser permits it. Popup opens the Popup block matching the Target Popup ID.
   * @polyDefault link
   * @polyTranslatable false
   */
  item2CtaAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  item2CtaTargetPopupId?: string;
  item2Cta2Text?: string;
  /** @polyType Url */
  item2Cta2Href?: string;
  /**
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyTranslatable false
   */
  item2Cta2Behavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Link by default. Watch video plays the URL in a player on this page. Download downloads the file when the browser permits it. Popup opens the Popup block matching the Target Popup ID.
   * @polyDefault link
   * @polyTranslatable false
   */
  item2Cta2Action?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  item2Cta2TargetPopupId?: string;
  item2Cta3Text?: string;
  /** @polyType Url */
  item2Cta3Href?: string;
  /**
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyTranslatable false
   */
  item2Cta3Behavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Link by default. Watch video plays the URL in a player on this page. Download downloads the file when the browser permits it. Popup opens the Popup block matching the Target Popup ID.
   * @polyDefault link
   * @polyTranslatable false
   */
  item2Cta3Action?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  item2Cta3TargetPopupId?: string;
  item2OverlayText?: string;
  /**
   * @polyType SelectList
   * @polyOptions Top Left=top-left,Top Right=top-right,Bottom Left=bottom-left,Bottom Right=bottom-right
   */
  item2OverlayPosition?: ContentGridOverlayPosition;

  /** Item 3 */
  item3Heading?: string;
  /** @polyType RichText */
  item3Text?: string;
  /** @polyType Url */
  item3IconUrl?: string;
  /** @polyType Url */
  item3ImageSrc?: string;
  item3ImageAlt?: string;
  item3Badge?: string;
  item3CtaText?: string;
  /** @polyType Url */
  item3CtaHref?: string;
  /**
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyTranslatable false
   */
  item3CtaBehavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Link by default. Watch video plays the URL in a player on this page. Download downloads the file when the browser permits it. Popup opens the Popup block matching the Target Popup ID.
   * @polyDefault link
   * @polyTranslatable false
   */
  item3CtaAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  item3CtaTargetPopupId?: string;
  item3Cta2Text?: string;
  /** @polyType Url */
  item3Cta2Href?: string;
  /**
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyTranslatable false
   */
  item3Cta2Behavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Link by default. Watch video plays the URL in a player on this page. Download downloads the file when the browser permits it. Popup opens the Popup block matching the Target Popup ID.
   * @polyDefault link
   * @polyTranslatable false
   */
  item3Cta2Action?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  item3Cta2TargetPopupId?: string;
  item3Cta3Text?: string;
  /** @polyType Url */
  item3Cta3Href?: string;
  /**
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyTranslatable false
   */
  item3Cta3Behavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Link by default. Watch video plays the URL in a player on this page. Download downloads the file when the browser permits it. Popup opens the Popup block matching the Target Popup ID.
   * @polyDefault link
   * @polyTranslatable false
   */
  item3Cta3Action?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  item3Cta3TargetPopupId?: string;
  item3OverlayText?: string;
  /**
   * @polyType SelectList
   * @polyOptions Top Left=top-left,Top Right=top-right,Bottom Left=bottom-left,Bottom Right=bottom-right
   */
  item3OverlayPosition?: ContentGridOverlayPosition;

  /** Item 4 */
  item4Heading?: string;
  /** @polyType RichText */
  item4Text?: string;
  /** @polyType Url */
  item4IconUrl?: string;
  /** @polyType Url */
  item4ImageSrc?: string;
  item4ImageAlt?: string;
  item4Badge?: string;
  item4CtaText?: string;
  /** @polyType Url */
  item4CtaHref?: string;
  /**
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyTranslatable false
   */
  item4CtaBehavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Link by default. Watch video plays the URL in a player on this page. Download downloads the file when the browser permits it. Popup opens the Popup block matching the Target Popup ID.
   * @polyDefault link
   * @polyTranslatable false
   */
  item4CtaAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  item4CtaTargetPopupId?: string;
  item4Cta2Text?: string;
  /** @polyType Url */
  item4Cta2Href?: string;
  /**
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyTranslatable false
   */
  item4Cta2Behavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Link by default. Watch video plays the URL in a player on this page. Download downloads the file when the browser permits it. Popup opens the Popup block matching the Target Popup ID.
   * @polyDefault link
   * @polyTranslatable false
   */
  item4Cta2Action?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  item4Cta2TargetPopupId?: string;
  item4Cta3Text?: string;
  /** @polyType Url */
  item4Cta3Href?: string;
  /**
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyTranslatable false
   */
  item4Cta3Behavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Link by default. Watch video plays the URL in a player on this page. Download downloads the file when the browser permits it. Popup opens the Popup block matching the Target Popup ID.
   * @polyDefault link
   * @polyTranslatable false
   */
  item4Cta3Action?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  item4Cta3TargetPopupId?: string;
  item4OverlayText?: string;
  /**
   * @polyType SelectList
   * @polyOptions Top Left=top-left,Top Right=top-right,Bottom Left=bottom-left,Bottom Right=bottom-right
   */
  item4OverlayPosition?: ContentGridOverlayPosition;

  /** Item 5 */
  item5Heading?: string;
  /** @polyType RichText */
  item5Text?: string;
  /** @polyType Url */
  item5IconUrl?: string;
  /** @polyType Url */
  item5ImageSrc?: string;
  item5ImageAlt?: string;
  item5Badge?: string;
  item5CtaText?: string;
  /** @polyType Url */
  item5CtaHref?: string;
  /**
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyTranslatable false
   */
  item5CtaBehavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Link by default. Watch video plays the URL in a player on this page. Download downloads the file when the browser permits it. Popup opens the Popup block matching the Target Popup ID.
   * @polyDefault link
   * @polyTranslatable false
   */
  item5CtaAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  item5CtaTargetPopupId?: string;
  item5Cta2Text?: string;
  /** @polyType Url */
  item5Cta2Href?: string;
  /**
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyTranslatable false
   */
  item5Cta2Behavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Link by default. Watch video plays the URL in a player on this page. Download downloads the file when the browser permits it. Popup opens the Popup block matching the Target Popup ID.
   * @polyDefault link
   * @polyTranslatable false
   */
  item5Cta2Action?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  item5Cta2TargetPopupId?: string;
  item5Cta3Text?: string;
  /** @polyType Url */
  item5Cta3Href?: string;
  /**
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyTranslatable false
   */
  item5Cta3Behavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Link by default. Watch video plays the URL in a player on this page. Download downloads the file when the browser permits it. Popup opens the Popup block matching the Target Popup ID.
   * @polyDefault link
   * @polyTranslatable false
   */
  item5Cta3Action?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  item5Cta3TargetPopupId?: string;
  item5OverlayText?: string;
  /**
   * @polyType SelectList
   * @polyOptions Top Left=top-left,Top Right=top-right,Bottom Left=bottom-left,Bottom Right=bottom-right
   */
  item5OverlayPosition?: ContentGridOverlayPosition;

  /** Item 6 */
  item6Heading?: string;
  /** @polyType RichText */
  item6Text?: string;
  /** @polyType Url */
  item6IconUrl?: string;
  /** @polyType Url */
  item6ImageSrc?: string;
  item6ImageAlt?: string;
  item6Badge?: string;
  item6CtaText?: string;
  /** @polyType Url */
  item6CtaHref?: string;
  /**
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyTranslatable false
   */
  item6CtaBehavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Link by default. Watch video plays the URL in a player on this page. Download downloads the file when the browser permits it. Popup opens the Popup block matching the Target Popup ID.
   * @polyDefault link
   * @polyTranslatable false
   */
  item6CtaAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  item6CtaTargetPopupId?: string;
  item6Cta2Text?: string;
  /** @polyType Url */
  item6Cta2Href?: string;
  /**
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyTranslatable false
   */
  item6Cta2Behavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Link by default. Watch video plays the URL in a player on this page. Download downloads the file when the browser permits it. Popup opens the Popup block matching the Target Popup ID.
   * @polyDefault link
   * @polyTranslatable false
   */
  item6Cta2Action?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  item6Cta2TargetPopupId?: string;
  item6Cta3Text?: string;
  /** @polyType Url */
  item6Cta3Href?: string;
  /**
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyTranslatable false
   */
  item6Cta3Behavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Link by default. Watch video plays the URL in a player on this page. Download downloads the file when the browser permits it. Popup opens the Popup block matching the Target Popup ID.
   * @polyDefault link
   * @polyTranslatable false
   */
  item6Cta3Action?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  item6Cta3TargetPopupId?: string;
  item6OverlayText?: string;
  /**
   * @polyType SelectList
   * @polyOptions Top Left=top-left,Top Right=top-right,Bottom Left=bottom-left,Bottom Right=bottom-right
   */
  item6OverlayPosition?: ContentGridOverlayPosition;
}

/**
 * @polySemanticUsage About this block: a flexible layout block that arranges multiple content
 * items (text, icons, images) in a structured grid. Use it for feature lists, benefit
 * breakdowns, or multi-point explanations where items are equal in weight and don't require
 * individual card styling.
 * ContentGrid vs CardGrid — scan ALL items in the source section as one unit: if any
 * single item carries multiple text-link CTAs, the entire section is a ContentGrid. If every
 * item has exactly one CTA and the whole card is the click target, use CardGrid instead.
 * ContentGrid's intent is featured education with explicit text CTAs; CardGrid's is a
 * substantive overview within self-contained cards. Each ContentGrid item has up to three
 * dedicated CTA slots (item{N}CtaText/Href, item{N}Cta2Text/Href, item{N}Cta3Text/Href),
 * rendered as a stacked list of arrow links; put the item's primary action in the first slot
 * and additional actions in slots 2-3. Set a slot's item{N}Cta{M}Action to video when the source
 * link plays a video ("Watch overview", play icon) and to download when it downloads a file
 * (PDF guide, download icon); every other link stays link. If the source has more than three links for one item,
 * carry the extras as markdown hyperlinks inside item{N}Text (they render as inline links) —
 * never drop them.
 * Item text is markdown: keep the source's inline links as [label](url) and keep its paragraph
 * breaks as blank lines — each paragraph renders as its own paragraph inside the card. Never
 * flatten multi-paragraph source copy into one run of text or strip its links.
 * Single-item pattern: a single-column, single-item ContentGrid is valid for one
 * content moment with an image; the media renders ABOVE the text (for media BESIDE text as a
 * card, use a single-card CardGrid). Do not use a single-column ContentGrid when any item's
 * media is an icon: the block renders at two columns instead, so author columns=2 directly
 * for icon items.
 * Image aspect: landscape (2:1) is the default; portrait (4:5) is the only aspect that
 * supports an overlay pill; square (1:1) is for 4-across photo grids where the source shows
 * equal-sided images.
 * Section title and subtitle: any heading/intro text that introduces the grid in the
 * source MUST map to this block's own heading and subheading props — they render in a section
 * header directly above the grid items. Never emit a separate RichText (or other text) block to
 * carry the grid's title or subtitle; that splits one section into two blocks and loses the
 * built-in header styling. The same applies to sectionBadge (pill above the heading) and
 * sectionCtaText/sectionCtaHref (CTA rendered alongside the heading).
 * Overlay text pills render only on portrait photo items: an item{N}OverlayText
 * is shown only when imageAspect is portrait and the item has item{N}ImageSrc — overlays authored
 * on a landscape grid are silently ignored. Each pill is positioned independently on its own
 * item's image (all default to top-left), so set item{N}OverlayPosition explicitly whenever the
 * source places a pill somewhere other than the top-left corner.
 * Link behavior: always leave item{N}CtaBehavior / item{N}Cta2Behavior / item{N}Cta3Behavior /
 * sectionCtaBehavior unset (same tab), even when that source link opens in a new tab or is an
 * External Link type. Migration never sets newTab; editors add it by hand for the few exceptions.
 * Never infer it from the destination host.
 */
export interface ContentGridProps extends ContentGridSlotProps {
  /** Badge pill text shown above the heading (e.g. "New Seller Incentives") */
  sectionBadge?: string;
  /**
   * CustomIcon name rendered inside the badge pill (default: "star")
   * @polyType SelectList
   * @polyOptions Cart=cart,Storefront=storefront,Package=package,Analytics=analytics,Star=star,Check=check,Arrow Forward=arrow-forward,Lock=lock,Menu=menu,Play=play,Search=search,Close=close,Globe=globe,Language=language,Arrow Left=arrow-left,Chevron Down=chevron-down,Chevron Right=chevron-right
   * @polyDefault star
   * @polyTranslatable false
   */
  sectionBadgeIcon?: CustomIconName;
  /** Section heading */
  heading?: string;
  /** Section subheading */
  subheading?: string;
  /**
   * CTA button label rendered next to the heading
   * @polyHelp Keep it to 2 to 5 words (about 25 characters, 40 at most). Translations can run longer, and long labels wrap onto extra lines.
   */
  sectionCtaText?: string;
  /**
   * CTA button href
   * @polyType Url
   */
  sectionCtaHref?: string;
  /**
   * Whether the section CTA opens in the current tab or a new one
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyTranslatable false
   */
  sectionCtaBehavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Link by default. Watch video plays the URL in a player on this page. Download downloads the file when the browser permits it. Popup opens the Popup block matching the Target Popup ID.
   * @polyDefault link
   * @polyTranslatable false
   */
  sectionCtaAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  sectionCtaTargetPopupId?: string;
  /**
   * Number of columns at desktop (default 3). columns=1 suits photo and text
   * items; if any item uses an icon, the grid renders at 2 columns instead.
   * At 4 columns the grid steps down to 2-up in the tablet-landscape range
   * before collapsing to 1-up on tablet/mobile.
   * @polyType Number
   */
  columns?: 1 | 2 | 3 | 4;
  /**
   * Placement of the section header relative to the items.
   * 'grid' (default) puts the header above the grid at full width.
   * 'sticky-rail' puts the header in a 448px left column that stays pinned
   * while the items scroll in a 1-up stack on the right, collapsing to a single
   * stacked column at the tablet breakpoint and below. `columns` carries no
   * meaning in this layout.
   * @polyType SelectList
   * @polyOptions Grid=grid,Sticky left rail=sticky-rail
   * @polyDefault grid
   */
  layout?: ContentGridLayout;
  /**
   * Portrait visual pinned beneath the header inside the sticky rail.
   * Ignored when layout is 'grid'.
   * @polyType Url
   */
  railImageSrc?: string;
  /**
   * Alt text for the rail visual. Unlike item images, this slot defaults to
   * decorative: it sits directly beneath the section heading and subheading,
   * which already carry the section's meaning, so an unlabelled rail image is
   * atmosphere rather than lost content and does not trigger the dev warning
   * that an unlabelled, heading-less item image does. Author alt text only
   * when the picture conveys something the copy does not.
   */
  railImageAlt?: string;
  /**
   * Background color
   * @polyOptions White=white,Off-White=off-white
   */
  background?: 'white' | 'off-white';
  /**
   * Hero image aspect: 'landscape' (2:1), 'portrait' (4:5, supports an overlay pill),
   * or 'square' (1:1, intended for 4-across photo grids). Overlay pills render on
   * portrait only.
   * @polyOptions Landscape=landscape,Portrait (4:5)=portrait,Square (1:1)=square
   */
  imageAspect?: 'landscape' | 'portrait' | 'square';
  /**
   * Icon rendering style. 'default' renders 72px icons; 'step' renders 48px
   * numbered step badges. In step mode the badge is either an auto-generated
   * numeral (see `stepNumbers`) or a per-item SVG supplied via item{N}IconUrl.
   * Used by the Steps section preset.
   * @polyOptions Default=default,Step=step
   */
  iconStyle?: 'default' | 'step';
  /**
   * Forwarded by the StepsSection preset. When true (and iconStyle='step'),
   * each step renders an auto-generated numbered circle (1, 2, 3, …) instead
   * of the per-item item{N}IconUrl SVG. On the off-white background the circle
   * is painted white for contrast. No effect outside step mode.
   */
  stepNumbers?: boolean;
  /**
   * Anchor ID for in-page links: a link to `#<anchorId>` on the same page scrolls to this block.
   * Normalised to letters, numbers and dashes (spaces become dashes, a leading `#` and other
   * characters are dropped). Must be unique on the page. Migration: when any link on the source page points at `#<id>` and that id sits on the
   * source section this block replaces, copy the id here unchanged (without the `#`).
   * @polyType Text
   * @polyLabel Anchor ID
   * @polyHelp Lets in-page links such as #your-id jump to this block. Use letters, numbers and dashes (other characters are removed); must be unique on the page.
   * @polyTranslatable false
   */
  anchorId?: string;
}

/**
 * Assemble a grid item from the flat props for a given slot (1-6).
 *
 * A slot counts as occupied when it carries a heading, body text, or an image.
 * Gating on the heading alone would silently drop a text-only item, which
 * migrated content depends on when a step has body copy but no title.
 */
function getItem(props: ContentGridSlotProps, slot: 1 | 2 | 3 | 4 | 5 | 6): ContentGridItem | null {
  const heading = props[`item${slot}Heading`];
  const text = props[`item${slot}Text`] ?? '';
  const imageSrc = props[`item${slot}ImageSrc`];
  if (!heading && !text && !imageSrc) return null;
  const ctaCandidates: Array<Partial<ContentGridItemCta>> = [
    {
      text: props[`item${slot}CtaText`],
      href: props[`item${slot}CtaHref`],
      behavior: props[`item${slot}CtaBehavior`],
      action: props[`item${slot}CtaAction`],
      popupTargetId: props[`item${slot}CtaTargetPopupId`],
    },
    {
      text: props[`item${slot}Cta2Text`],
      href: props[`item${slot}Cta2Href`],
      behavior: props[`item${slot}Cta2Behavior`],
      action: props[`item${slot}Cta2Action`],
      popupTargetId: props[`item${slot}Cta2TargetPopupId`],
    },
    {
      text: props[`item${slot}Cta3Text`],
      href: props[`item${slot}Cta3Href`],
      behavior: props[`item${slot}Cta3Behavior`],
      action: props[`item${slot}Cta3Action`],
      popupTargetId: props[`item${slot}Cta3TargetPopupId`],
    },
  ];
  const ctas = ctaCandidates.filter((cta): cta is ContentGridItemCta => Boolean(
    cta.text && hasCtaDestination(cta.href, cta.action, cta.popupTargetId),
  ));
  return {
    heading,
    text,
    iconUrl: props[`item${slot}IconUrl`],
    imageSrc,
    imageAlt: props[`item${slot}ImageAlt`] ?? '',
    badge: props[`item${slot}Badge`],
    ctas,
    overlayText: props[`item${slot}OverlayText`],
    overlayPosition: props[`item${slot}OverlayPosition`],
  };
}

export const ContentGrid: React.FC<ContentGridProps> = ({
  sectionBadge,
  sectionBadgeIcon,
  heading,
  subheading,
  sectionCtaText,
  sectionCtaHref,
  sectionCtaBehavior,
  sectionCtaAction,
  sectionCtaTargetPopupId,
  columns = 3,
  layout = 'grid',
  railImageSrc,
  railImageAlt,
  background = 'white',
  imageAspect = 'landscape',
  iconStyle = 'default',
  stepNumbers = false,
  anchorId,
  ...slotProps
}) => {
  const items = ([1, 2, 3, 4, 5, 6] as const)
    .map(slot => getItem(slotProps as ContentGridSlotProps, slot))
    .filter((item): item is ContentGridItem => item !== null);

  const isStickyRail = layout === 'sticky-rail';
  // A single-column layout only suits photo or text items: a 72px icon over
  // full-width text reads as a broken layout. Rather than rendering nothing
  // (a blank gap that neither QA nor the author can see), fall back to the
  // nearest supported layout, two columns, and say so in development. The
  // section carries data-poly-fallback so the substitution is discoverable in
  // DOM inspection. The sticky rail is exempt: its right column is 1-up by
  // design and ignores `columns` entirely.
  const hasIconItem = items.some(item => !!item.iconUrl);
  const fallsBackToTwoColumns = columns === 1 && hasIconItem && !isStickyRail;
  const resolvedColumns = fallsBackToTwoColumns ? 2 : columns;
  if (fallsBackToTwoColumns && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(
      '[ContentGrid] columns=1 supports photo and text items only. An item uses an icon, so the grid is rendering at 2 columns. Set columns to 2 or more, or replace the icon with an image.',
    );
  }

  const bgClass = background === 'off-white' ? ' content-grid--off-white' : '';
  // The rail's right column is always 1-up, so a column-count modifier would
  // only compete with the rail's own item sizing.
  const colClass = isStickyRail ? '' : ` content-grid--${resolvedColumns}col`;
  const layoutClass = isStickyRail ? ' content-grid--sticky-rail' : '';
  const isPhotoGrid = items.length > 0 && items.every(item => !!item.imageSrc && !item.iconUrl);
  const mediaClass = isPhotoGrid ? ' content-grid--photo' : '';
  const stepClass = iconStyle === 'step' ? ' content-grid--step' : '';
  const headingId = useId();
  const labelledBy = heading ? headingId : undefined;
  const isPortrait = imageAspect === 'portrait';
  const isSquare = imageAspect === 'square';

  // An image-only item has no heading to fall back on, so an unauthored
  // item{N}ImageAlt renders alt="" — which tells assistive tech the image is
  // decorative and skippable. Grid images carry meaning, so surface it to the
  // author rather than silently hiding content.
  if (process.env.NODE_ENV !== 'production') {
    const undescribed = items.filter(item => item.imageSrc && !item.imageAlt && !item.heading);
    if (undescribed.length > 0) {
      // eslint-disable-next-line no-console
      console.warn(
        `[ContentGrid] ${undescribed.length} item(s) have an image but no alt text and no heading, so they render alt="" and are hidden from screen readers. Author item{N}ImageAlt for any image that carries meaning.`,
      );
    }
    // The rail visual only mounts inside the sticky-rail branch, so an author
    // who fills Rail Image under the grid layout sees nothing and has no cue
    // that Layout is the reason.
    if (railImageSrc && !isStickyRail) {
      // eslint-disable-next-line no-console
      console.warn(
        '[ContentGrid] railImageSrc is set but layout is "grid", so the rail visual is not rendered. Set layout="sticky-rail" to show it.',
      );
    }
  }

  const header = (
    <SectionHeader
      badge={sectionBadge}
      badgeIcon={sectionBadgeIcon}
      heading={heading}
      headingId={labelledBy}
      subheading={subheading}
      ctaText={sectionCtaText}
      ctaHref={sectionCtaHref}
      ctaBehavior={sectionCtaBehavior}
      ctaAction={sectionCtaAction}
      ctaTargetPopupId={sectionCtaTargetPopupId}
      className="content-grid__header"
    />
  );

  return (
    <section
      {...blockAnchorAttributes(anchorId)}
      className={`content-grid${bgClass}${colClass}${mediaClass}${stepClass}${layoutClass}`}
      aria-labelledby={labelledBy}
      data-poly-fallback={fallsBackToTwoColumns ? 'content-grid-columns-fallback' : undefined}
      data-poly-fallback-detail={fallsBackToTwoColumns ? 'columns=1 with icon items rendered at 2 columns' : undefined}
    >
      <div className="content-grid__container">
        {isStickyRail ? (
          <div className="content-grid__rail">
            {header}
            {railImageSrc && (
              <div className="content-grid__rail-visual">
                <Image
                  bare
                  url={railImageSrc}
                  alt={railImageAlt ?? ''}
                  className="content-grid__rail-img"
                />
              </div>
            )}
          </div>
        ) : (
          header
        )}

        {/* Grid of items — kept inline rather than factored into its own
            component since ContentGrid is the only consumer. */}
        <div className="content-grid__grid">
          {items.map((item, index) => {
            const isPhoto = !!(item.imageSrc && !item.iconUrl);
            const imageSrc = item.imageSrc;
            // Overlay only renders for portrait photo items with overlayText.
            // Landscape images forbid overlays, so overlayText is ignored
            // when imageAspect='landscape'.
            const showOverlay = isPhoto && isPortrait && !!item.overlayText;
            const overlayPosition = item.overlayPosition ?? 'top-left';
            const showStepNumber = iconStyle === 'step' && stepNumbers;
            return (
              <article key={`${item.heading ?? 'item'}-${index}`} className="content-grid__item">
                {showStepNumber ? (
                  <div
                    className="content-grid__icon content-grid__step-number"
                    role="img"
                    aria-label={`Step ${index + 1}`}
                  >
                    {index + 1}
                  </div>
                ) : (
                  item.iconUrl && (
                    <div
                      className="content-grid__icon"
                      aria-hidden={iconStyle === 'step' ? undefined : true}
                    >
                      <Image
                        bare
                        url={item.iconUrl}
                        alt={iconStyle === 'step' ? `Step ${index + 1}` : ''}
                        className="content-grid__icon-img"
                      />
                    </div>
                  )
                )}

                {isPhoto && imageSrc && (
                  <div
                    className={`content-grid__photo${isPortrait ? ' content-grid__photo--portrait' : ''}${isSquare ? ' content-grid__photo--square' : ''}`}
                  >
                    <Image
                      url={imageSrc}
                      alt={item.imageAlt || item.heading || ''}
                      objectFit="cover"
                    />
                    {showOverlay && (
                      <span
                        className={`content-grid__photo-overlay content-grid__photo-overlay--${overlayPosition}`}
                      >
                        {item.overlayText}
                      </span>
                    )}
                  </div>
                )}

                <div className="content-grid__text">
                  {item.badge && (
                    <span className="content-grid__badge">{item.badge}</span>
                  )}
                  {item.heading && (
                    <h3 className="content-grid__heading">
                      {item.heading}
                    </h3>
                  )}
                  {item.text && (
                    <div className="content-grid__description">
                      <ReactMarkdown
                        components={{
                          a: ({ href, children }) => (
                            <Link href={href ?? ''} variant="secondary" fontSize="inherit">
                              {children}
                            </Link>
                          ),
                        }}
                      >
                        {item.text}
                      </ReactMarkdown>
                    </div>
                  )}
                  {item.ctas.length > 0 && (
                    <div className="content-grid__link">
                      {item.ctas.map((cta, ctaIndex) => (
                        <ArrowLink
                          key={`${cta.href ?? cta.popupTargetId}-${ctaIndex}`}
                          href={cta.href}
                          behavior={cta.behavior}
                          action={cta.action}
                          popupTargetId={cta.popupTargetId}
                        >
                          {cta.text}
                        </ArrowLink>
                      ))}
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
